import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError } from '../api';

export function Banner({
  tone,
  title,
  children,
  items,
}: {
  tone: 'warn' | 'error' | 'info' | 'good';
  title?: string;
  children?: ReactNode;
  items?: string[];
}) {
  const icon = tone === 'error' ? '!' : tone === 'warn' ? '!' : tone === 'good' ? '✓' : 'i';
  return (
    <div className={`banner ${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <span className="icon" aria-hidden>
        {icon}
      </span>
      <div style={{ minWidth: 0 }}>
        {title && <strong>{title}</strong>}
        {children}
        {items && items.length > 0 && (
          <ul>
            {items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function Pill({ tone, children }: { tone?: 'ok' | 'bad' | 'warn' | 'off'; children: ReactNode }) {
  return <span className={`pill${tone ? ` ${tone}` : ''}`}>{children}</span>;
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty-state">
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}

export function Field({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="label">{label}</span>
      {children}
      {help && <span className="help">{help}</span>}
    </label>
  );
}

/**
 * Minimal async data hook.
 *
 * `stale` is exposed rather than swapping in a skeleton on refetch: holding the
 * previous render at reduced opacity avoids the layout jump that makes a
 * dashboard feel unreliable.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []): {
  data: T | null;
  error: string | null;
  loading: boolean;
  stale: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loader()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, error, loading, stale: loading && data !== null, reload };
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--ink-2)', fontSize: 13 }}>
      <span className="spinner" aria-hidden /> {label ?? 'Working…'}
    </span>
  );
}

/** JSON editor with live validation — used for templates and transform pipelines. */
export function JsonEditor({
  value,
  onChange,
  rows = 10,
  label,
  help,
}: {
  value: unknown;
  onChange: (parsed: any, valid: boolean) => void;
  rows?: number;
  label: string;
  help?: string;
}) {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2));
  const [problem, setProblem] = useState<string | null>(null);

  return (
    <div>
      <Field label={label} help={help}>
        <textarea
          rows={rows}
          value={text}
          spellCheck={false}
          onChange={(e) => {
            setText(e.target.value);
            try {
              const parsed = JSON.parse(e.target.value || 'null');
              setProblem(null);
              onChange(parsed, true);
            } catch (err: any) {
              // Report the parse error rather than silently keeping the last good
              // value — an editor who thinks a change saved when it did not is
              // how a report quietly stops matching its definition.
              setProblem(err?.message ?? 'Invalid JSON');
              onChange(null, false);
            }
          }}
        />
      </Field>
      {problem && (
        <p style={{ margin: '-6px 0 12px', fontSize: 11.5, color: 'var(--critical)' }}>Not valid JSON: {problem}</p>
      )}
    </div>
  );
}
